import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Field, GdprConsent, FormSuccess, inputCls } from "./FormBits";

const initial = { name: "", email: "", phone: "", subject: "", message: "", gdpr_consent: false };

export const ContactForm = () => {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email address.";
    if (form.subject.trim().length < 2) e.subject = "Please enter a subject.";
    if (form.message.trim().length < 2) e.message = "Please enter a message.";
    if (!form.gdpr_consent) e.gdpr_consent = "GDPR consent is required to proceed.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Message sent");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  if (done)
    return (
      <FormSuccess
        title="Thank you for reaching out"
        message="We have received your message and will respond as soon as possible."
        onReset={() => { setForm(initial); setDone(false); }}
        resetLabel="Send another message"
      />
    );

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="contact-form" noValidate>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Name" required error={errors.name}>
          <input className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="contact-name" />
        </Field>
        <Field label="Email" required error={errors.email}>
          <input type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="contact-email" />
        </Field>
        <Field label="Phone">
          <input className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="contact-phone" />
        </Field>
        <Field label="Subject" required error={errors.subject}>
          <input className={inputCls} value={form.subject} onChange={(e) => set("subject", e.target.value)} data-testid="contact-subject" />
        </Field>
      </div>
      <Field label="Message" required error={errors.message}>
        <textarea rows={5} className={inputCls} value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="contact-message" />
      </Field>
      <GdprConsent checked={form.gdpr_consent} onChange={(v) => set("gdpr_consent", v)} error={errors.gdpr_consent} />
      <button type="submit" disabled={loading} className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-navy px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-navy-light disabled:opacity-70 sm:w-auto" data-testid="contact-submit">
        {loading && <Loader2 className="h-4 w-4 animate-spin" />} Send Message
      </button>
    </form>
  );
};

export default ContactForm;
